<template>
  <div class="eooker-style">
    <el-container>
      <!-- 简洁的header -->
      <el-header class="page-header">
        <h1>{{ this.prodHeader }}</h1>
      </el-header>

      <el-main class="main-content">
        <!-- 加载状态 -->
        <template v-if="!products.length">
          <div class="loading-state">
            <div class="loading-spinner">
              <div class="spinner-ring"></div>
              <div class="spinner-ring"></div>
              <div class="spinner-ring"></div>
            </div>
            <p class="loading-text">Loading products...</p>
            <div class="loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </template>

        <!-- 产品展示区域 -->
        <template v-else>
          <div v-for="(product, index) in products" :key="index" class="product-container">
            <!-- 产品详情卡片 -->
            <div class="product-card">
              <!-- 左侧：图片展示区 -->
              <div class="product-left">
                <div class="main-image-wrapper" @click="openPreview(index)">
                  <img :key="currentImage(index)" :src="currentImage(index)" :alt="product.prodTitle" class="main-product-image" />
                </div>
                
                <!-- 缩略图 -->
                <div class="thumbnail-list">
                  <div 
                    v-for="(img, imgIndex) in product.prodImgurl" 
                    :key="imgIndex"
                    class="thumbnail-item"
                    :class="{ active: currentImageIndex[index] === imgIndex }"
                    @mouseenter="selectImage(index, imgIndex)"
                  >
                    <img :src="img.src" :alt="img.alt" />
                  </div>
                </div>
              </div>

              <!-- 右侧：产品信息 -->
              <div class="product-right">
                <div class="model-tag">MODEL DETAILS</div>
                
                <h1 class="product-title">{{ product.prodTitle }}</h1>
                
                <!-- 如果有 prodSku，显示 SKU 和 UPC -->
                <template v-if="product.prodSku">
                  <div class="price-section">
                    <span class="current-price">${{ product.prodPrice }}</span>
                  </div>

                  <div class="product-info">
                    <div class="info-row">
                      <span class="info-label">SKU:</span>
                      <span class="info-value">{{ product.prodSku }}</span>
                    </div>
                    <div class="info-row" v-if="product.prodUpc">
                      <span class="info-label">UPC:</span>
                      <span class="info-value">{{ product.prodUpc }}</span>
                    </div>
                  </div>
                </template>

                <!-- 如果没有 prodSku，显示原价和折扣 -->
                <template v-else>
                  <div class="price-section">
                    <span class="current-price">${{ product.prodPrice }}</span>
                  </div>

                  <div class="product-info" v-if="product.prodOriginalPrice || product.prodDiscount">
                    <div class="info-row" v-if="product.prodOriginalPrice">
                      <span class="info-label">List Price:</span>
                      <span class="info-value original-price-text">${{ product.prodOriginalPrice }}</span>
                    </div>
                    <div class="info-row" v-if="product.prodDiscount">
                      <span class="info-label">Discount:</span>
                      <span class="info-value discount-value">{{ product.prodDiscount }}% OFF</span>
                    </div>
                  </div>
                </template>

                <!-- 操作按钮 -->
                <div class="action-buttons">
                  <el-button class="add-to-cart-btn" @click="addToCart(product, $event)" v-if="product.prodSku">
                    Add to Cart
                  </el-button>
                  <el-button class="learn-more-btn" @click="learnMore(product)" v-if="!product.prodSku && product.prodAmazonurl">
                    Learn more
                  </el-button>
                </div>
              </div>
            </div>

            <!-- 视频展示区 -->
            <section class="video-section" v-if="product.prodVideourl">
              <div class="video-wrapper">
                <iframe
                  :src="product.prodVideourl"
                  frameborder="0"
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowfullscreen
                  loading="lazy"
                  scrolling="no"
                ></iframe>
              </div>
            </section>

            <!-- 产品亮点 -->
            <section class="product-highlights" v-if="product.prodContent">
              <h2 class="highlights-title">Product Highlights</h2>
              <div class="highlights-content" v-html="formatHighlights(product.prodContent)"></div>
            </section>

            <!-- 产品分隔线 -->
            <div class="product-divider" v-if="index < products.length - 1"></div>
          </div>
        </template>

        <!-- 联系我们 -->
        <section class="contact-section">
          <h2>Contact Us</h2>
          <p>Thank you for your support and we sincerely invite you to be our product experience officer. Product Experience Officers can obtain our products for free. After using them, please give us feedback on your actual usage experience.</p>
          <el-button class="contact-btn" @click="sendEmail">{{ prodEmail }}</el-button>
        </section>
      </el-main>
    </el-container>

    <!-- 图片预览对话框 -->
    <el-dialog
      :visible.sync="previewVisible"
      :append-to-body="true"
      custom-class="preview-dialog"
      :show-close="false"
      @click.native="closePreview"
    >
      <img :src="currentPreviewImage" class="preview-image" @click.stop />
      <div class="preview-close" @click="closePreview">
        <i class="el-icon-close"></i>
      </div>
      <div class="preview-controls" v-if="currentProductImages.length > 1">
        <el-button circle class="preview-nav-btn prev-btn" @click.stop="prevPreviewImage" v-show="previewImageIndex > 0">
          <i class="el-icon-arrow-left"></i>
        </el-button>
        <el-button circle class="preview-nav-btn next-btn" @click.stop="nextPreviewImage" v-show="previewImageIndex < currentProductImages.length - 1">
          <i class="el-icon-arrow-right"></i>
        </el-button>
      </div>
    </el-dialog>

    <!-- 添加购物车动画组件 -->
    <div class="cart-animation" :class="{ 'animate': showCartAnimation }" :style="cartStyle">
      <i class="el-icon-shopping-cart-2"></i>
    </div>
  </div>
</template>

<script>
export default {
  name: "CompanyHome",
  data() {
    return {
      prodHeader: '',
      prodEmail: '',
      products: [],
      currentImageIndex: {},
      isAddingToCart: false,
      showCartAnimation: false,
      cartStyle: {
        left: '0px',
        top: '0px'
      },
      previewVisible: false,
      previewProductIndex: 0,
      previewImageIndex: 0
    };
  },
  computed: {
    currentProductImages() {
      return this.products[this.previewProductIndex]?.prodImgurl || [];
    },
    currentPreviewImage() {
      return this.currentProductImages[this.previewImageIndex]?.src || '';
    }
  },
  mounted() {
    this.init();
    this.select();
  },
  methods: {
    init() {
      const params = {
        visitDomain: window.location.hostname,
      }
      this.$axios.post('/visit/insert', params).then(response => {
        if (response.data.code === 0) {
          console.log();
        }
      }).catch(error => {
        console.error(error);
      });
    },
    select() {
      const params = {
        prodName: window.location.hostname.split('.')[1],
      }
      this.$axios.post('/prod/list', params).then(response => {
        if (response.data.code === 0) {
          const data = response.data.data;
          this.prodHeader = data[0].prodHeader;
          this.prodEmail = data[0].prodEmail;

          // 处理产品数据
          this.products = data.map((item, index) => {
            const imgurl = item.prodImgurl.split(",");
            const prodImgurlmap = imgurl.map((src, imgIndex) => ({
              src: src,
              alt: item.prodTitle + ' - Image ' + (imgIndex + 1)
            }));

            // 初始化当前图片索引
            this.$set(this.currentImageIndex, index, 0);

            return {
              ...item,
              prodImgurl: prodImgurlmap
            };
          });

          document.title = data[0].prodName;
        }
      }).catch(error => {
        console.error(error);
      });
    },
    // 获取当前显示的图片
    currentImage(productIndex) {
      const product = this.products[productIndex];
      const imgIndex = this.currentImageIndex[productIndex] || 0;
      return product?.prodImgurl[imgIndex]?.src || '';
    },
    // 选择图片
    selectImage(productIndex, imageIndex) {
      this.$set(this.currentImageIndex, productIndex, imageIndex);
    },
    sendEmail() {
      const subject = encodeURIComponent("Product Inquiry");
      const body = encodeURIComponent("Hi, I'm interested in your products...");
      const recipient = this.prodEmail;
      const mailtoLink = `mailto:${recipient}?subject=${subject}&body=${body}`;
      window.location.href = mailtoLink;
    },
    addToCart(product, event) {
      if (this.isAddingToCart) return;
      this.isAddingToCart = true;

      // 获取按钮位置
      const button = event.currentTarget;
      const buttonRect = button.getBoundingClientRect();

      // 设置动画起始位置
      this.cartStyle = {
        left: `${buttonRect.left + buttonRect.width / 2}px`,
        top: `${buttonRect.top + buttonRect.height / 2}px`
      };

      // 显示动画
      this.showCartAnimation = true;

      // 显示成功消息
      this.$message({
        message: 'Added to cart successfully!',
        type: 'success',
        duration: 2000
      });

      // 动画结束后重置状态
      setTimeout(() => {
        this.showCartAnimation = false;
        this.isAddingToCart = false;
      }, 1500);
    },
    learnMore(product) {
      if (product.prodAmazonurl) {
        window.open(product.prodAmazonurl, "_blank");
      }
    },
    openPreview(productIndex) {
      this.previewProductIndex = productIndex;
      this.previewImageIndex = this.currentImageIndex[productIndex] || 0;
      this.previewVisible = true;
    },
    closePreview() {
      this.previewVisible = false;
    },
    prevPreviewImage() {
      if (this.previewImageIndex > 0) {
        this.previewImageIndex--;
      }
    },
    nextPreviewImage() {
      if (this.previewImageIndex < this.currentProductImages.length - 1) {
        this.previewImageIndex++;
      }
    },
    formatHighlights(content) {
      // 将内容按段落分割，提取【】内的标题和描述
      const paragraphs = content.split('\n');
      return paragraphs
        .map(p => p.trim())
        .filter(p => p.length > 0)
        .map(p => {
          // 匹配【标题】格式
          const titleMatch = p.match(/【([^】]+)】/);
          if (titleMatch) {
            const title = titleMatch[1];
            const description = p.replace(/【[^】]+】/, '').trim();
            return `
              <div class="highlight-item">
                <div class="highlight-content">
                  <strong>【${title}】</strong> ${description}
                </div>
              </div>
            `;
          } else {
            return `
              <div class="highlight-item">
                <div class="highlight-content">
                  ${p}
                </div>
              </div>
            `;
          }
        })
        .join('');
    }
  }
};
</script>

<style scoped>
.eooker-style {
  --brand-primary: #0f172a;
  --brand-accent: #0071e3;
  --text-primary: #1e293b;
  --text-secondary: #475569;
  --bg-light: #f8fafc;
  --radius-sm: 6px;
  --radius-md: 8px;
  --radius-lg: 12px;
  --radius-xl: 16px;
  --shadow-sm: 0 1px 3px rgba(15, 23, 42, 0.08);
  --shadow-md: 0 4px 12px rgba(15, 23, 42, 0.1);
  --shadow-lg: 0 10px 24px rgba(15, 23, 42, 0.12);
  --transition-base: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  background-color: var(--bg-light);
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

.page-header {
  background: #fff;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  height: 60px !important;
  display: flex;
  align-items: center;
  justify-content: center;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: var(--shadow-sm);
}

.page-header h1 {
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  color: var(--brand-primary);
}

.main-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 20px;
}

/* 产品容器 */
.product-container {
  margin-bottom: 60px;
}

/* 产品卡片 */
.product-card {
  background: #fff;
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-md);
  border: 1px solid rgba(15, 23, 42, 0.08);
  padding: 40px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  margin-bottom: 40px;
  transition: var(--transition-base);
}

.product-card:hover {
  box-shadow: var(--shadow-lg);
}

/* 左侧图片区域 */
.product-left {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.main-image-wrapper {
  width: 600px;
  max-width: 100%;
  height: 600px;
  background: #fff;
  border-radius: var(--radius-lg);
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.main-product-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: var(--transition-base);
  animation: fadeInImage 0.3s ease-in-out;
}

@keyframes fadeInImage {
  0% {
    opacity: 0;
    transform: scale(0.98);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

.main-image-wrapper:hover .main-product-image {
  transform: scale(1.05);
}

/* 缩略图列表 */
.thumbnail-list {
  display: flex;
  gap: 12px;
  flex-wrap: nowrap;
  padding-bottom: 6px;
  overflow-x: auto;
  max-width: 600px;
  width: 100%;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.thumbnail-list::-webkit-scrollbar {
  display: none;
}

.thumbnail-item {
  position: relative;
  width: 82px;
  height: 82px;
  border: 2px solid transparent;
  border-radius: var(--radius-md);
  overflow: hidden;
  cursor: pointer;
  transition: var(--transition-base);
  background: #fff;
  box-shadow: var(--shadow-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.thumbnail-item::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  border: 2px solid var(--brand-accent);
  opacity: 0;
  transform: scale(0.9);
  transition: var(--transition-base);
  pointer-events: none;
}

.thumbnail-item:hover::after,
.thumbnail-item.active::after {
  opacity: 1;
  transform: scale(1);
}

.thumbnail-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 113, 227, 0.2);
}

.thumbnail-item img {
  max-width: 100%;
  max-height: 72px;
  object-fit: contain;
}

/* 右侧产品信息区域 */
.product-right {
  display: flex;
  flex-direction: column;
  gap: 20px;
  text-align: left;
  align-items: flex-start;
}

.model-tag {
  display: inline-block;
  background: rgba(0, 113, 227, 0.08);
  color: var(--brand-accent);
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  width: fit-content;
  text-transform: uppercase;
  text-align: left;
}

.product-title {
  font-size: 1.9rem;
  font-weight: 700;
  color: var(--brand-primary);
  line-height: 1.4;
  margin: 0;
  text-align: left;
  width: 100%;
}

.price-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 0;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  flex-wrap: wrap;
  gap: 16px;
}

.current-price {
  font-size: 1.8rem;
  font-weight: 700;
  color: #dc2626;
}

.listed-date {
  font-size: 0.9rem;
  color: var(--text-secondary);
  font-weight: 600;
  white-space: nowrap;
}

/* 产品信息 */
.product-info {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 0;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}

.info-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 0.95rem;
  line-height: 1.5;
}

.info-label {
  font-weight: 600;
  color: var(--text-secondary);
  min-width: 110px;
  flex-shrink: 0;
}

.info-value {
  color: var(--text-primary);
  flex: 1;
  word-break: break-word;
}

.original-price-text {
  text-decoration: line-through;
  color: var(--text-secondary);
  opacity: 0.7;
}

.discount-value {
  color: #ef4444;
  font-weight: 600;
}

/* 操作按钮 */
.action-buttons {
  display: flex;
  gap: 12px;
  margin-top: 8px;
  width: 100%;
  flex-wrap: wrap;
}

.add-to-cart-btn {
  background: var(--brand-accent) !important;
  color: #fff !important;
  border: none !important;
  border-radius: 999px !important;
  padding: 12px 28px !important;
  font-size: 1rem !important;
  font-weight: 600 !important;
  cursor: pointer !important;
  transition: var(--transition-base) !important;
  flex: 1;
  min-width: 150px;
}

.add-to-cart-btn:hover {
  background: #0056b3 !important;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 113, 227, 0.3) !important;
}

.learn-more-btn {
  background: transparent !important;
  color: var(--brand-accent) !important;
  border: 2px solid var(--brand-accent) !important;
  border-radius: 999px !important;
  padding: 12px 28px !important;
  font-size: 1rem !important;
  font-weight: 600 !important;
  cursor: pointer !important;
  transition: var(--transition-base) !important;
  flex: 1;
  min-width: 150px;
}

.learn-more-btn:hover {
  background: var(--brand-accent) !important;
  color: #fff !important;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 113, 227, 0.3) !important;
}

/* 购物车动画 */
.cart-animation {
  position: fixed;
  z-index: 9999;
  pointer-events: none;
  color: #fff;
  font-size: 28px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--brand-accent);
  box-shadow: 0 0 20px rgba(0, 113, 227, 0.6);
  opacity: 0;
  transform: translate(-50%, -50%) scale(0);
}

.cart-animation.animate {
  animation: cartFly 1.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

@keyframes cartFly {
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -300%) scale(0.5);
    opacity: 0;
  }
}

/* 图片预览对话框 */
:deep(.preview-dialog) {
  background: rgba(0, 0, 0, 0.95);
  border: none;
  border-radius: 0;
  margin: 0 !important;
  padding: 0;
  width: 100vw !important;
  height: 100vh !important;
  max-width: 100% !important;
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  transform: none !important;
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.preview-dialog .el-dialog__header),
:deep(.preview-dialog .el-dialog__body) {
  padding: 0;
  margin: 0;
  background: transparent;
}

.preview-image {
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  cursor: default;
  display: block;
  margin: 0 auto;
}

.preview-close {
  position: fixed;
  top: 30px;
  right: 30px;
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: var(--transition-base);
  z-index: 2001;
  font-size: 24px;
}

.preview-close:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.1) rotate(90deg);
  border-color: rgba(255, 255, 255, 0.5);
}

.preview-controls {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 2001;
}

.preview-nav-btn {
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.1) !important;
  border: 2px solid rgba(255, 255, 255, 0.3) !important;
  color: #fff !important;
  width: 56px !important;
  height: 56px !important;
  pointer-events: auto;
  transition: var(--transition-base) !important;
  cursor: pointer;
  font-size: 20px !important;
}

.preview-nav-btn.prev-btn {
  left: 30px;
}

.preview-nav-btn.next-btn {
  right: 30px;
}

.preview-nav-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
  transform: translateY(-50%) scale(1.1) !important;
  border-color: rgba(255, 255, 255, 0.5) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
}

.preview-nav-btn i {
  font-size: 22px;
}

.main-image-wrapper {
  cursor: zoom-in;
}

/* 视频部分 */
.video-section {
  background: #fff;
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-md);
  border: 1px solid rgba(15, 23, 42, 0.08);
  padding: 40px;
  margin-bottom: 40px;
  transition: var(--transition-base);
}

.video-section:hover {
  box-shadow: var(--shadow-lg);
}

.video-wrapper {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  border-radius: var(--radius-lg);
  overflow: hidden;
  background: #000;
}

.video-wrapper iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
}

/* 产品亮点 */
.product-highlights {
  background: #fff;
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-md);
  border: 1px solid rgba(15, 23, 42, 0.08);
  padding: 50px 60px;
  margin-bottom: 40px;
  transition: var(--transition-base);
  text-align: left;
}

.product-highlights:hover {
  box-shadow: var(--shadow-lg);
}

.highlights-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--brand-primary);
  margin: 0 0 32px 0;
  text-align: left;
  width: 100%;
}

.highlights-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: flex-start;
  width: 100%;
}

.highlight-item {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 16px;
  padding: 0;
  transition: var(--transition-base);
  width: 100%;
  text-align: left;
}

.check-icon {
  color: #7c3aed;
  font-size: 20px;
  font-weight: 700;
  margin-right: 8px;
  flex-shrink: 0;
}

.highlight-content {
  color: var(--text-primary);
  font-size: 1.125rem;
  line-height: 1.7;
  text-align: left;
  flex: 1;
  width: 100%;
  word-wrap: break-word;
}

.highlight-content strong {
  color: var(--brand-primary);
  font-weight: 700;
  display: inline;
  text-align: left;
}

/* 确保所有深层元素都左对齐 */
.product-highlights * {
  text-align: left;
}

.highlights-content :deep(*) {
  text-align: left;
}

/* 产品分隔线 */
.product-divider {
  height: 1px;
  background: linear-gradient(to right, transparent, rgba(15, 23, 42, 0.1), transparent);
  margin: 64px 0;
}

/* 联系我们部分 */
.contact-section {
  background: #fff;
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-md);
  border: 1px solid rgba(15, 23, 42, 0.08);
  padding: 60px 40px;
  text-align: center;
  margin-top: 40px;
  transition: var(--transition-base);
}

.contact-section:hover {
  box-shadow: var(--shadow-lg);
}

.contact-section h2 {
  font-size: 2rem;
  font-weight: 700;
  color: var(--brand-primary);
  margin-bottom: 16px;
}

.contact-section p {
  font-size: 1rem;
  color: var(--text-secondary);
  line-height: 1.7;
  max-width: 800px;
  margin: 0 auto 30px;
}

.contact-btn {
  background: var(--brand-accent) !important;
  color: #fff !important;
  border: none !important;
  border-radius: 999px !important;
  padding: 12px 28px !important;
  font-size: 1rem !important;
  font-weight: 600 !important;
  cursor: pointer !important;
  transition: var(--transition-base) !important;
  box-shadow: 0 4px 12px rgba(0, 113, 227, 0.2) !important;
}

.contact-btn:hover {
  background: #0056b3 !important;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 113, 227, 0.3) !important;
}

/* 加载状态 */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  color: var(--text-secondary);
  padding: 60px 20px;
}

.loading-spinner {
  position: relative;
  width: 64px;
  height: 64px;
  margin-bottom: 24px;
}

.spinner-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 3px solid transparent;
  border-top-color: var(--brand-accent);
  border-radius: 50%;
  animation: spin 1.2s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}

.spinner-ring:nth-child(2) {
  width: 75%;
  height: 75%;
  top: 12.5%;
  left: 12.5%;
  border-top-color: #3b82f6;
  animation-duration: 1.5s;
  animation-direction: reverse;
}

.spinner-ring:nth-child(3) {
  width: 50%;
  height: 50%;
  top: 25%;
  left: 25%;
  border-top-color: #60a5fa;
  animation-duration: 1.8s;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 12px;
  color: var(--brand-primary);
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

.loading-dots {
  display: flex;
  gap: 8px;
}

.loading-dots span {
  width: 10px;
  height: 10px;
  background: var(--brand-accent);
  border-radius: 50%;
  animation: dotBounce 1.4s ease-in-out infinite;
}

.loading-dots span:nth-child(1) {
  animation-delay: 0s;
}

.loading-dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.loading-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes dotBounce {
  0%, 80%, 100% {
    transform: translateY(0) scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: translateY(-12px) scale(1.2);
    opacity: 1;
  }
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .main-content {
    padding: 32px 20px;
  }

  .product-card {
    grid-template-columns: 1fr;
    gap: 36px;
    padding: 32px;
  }

  .main-image-wrapper {
    width: 100%;
    height: 420px;
    min-height: 360px;
  }

  .thumbnail-list {
    max-width: 100%;
  }

  .product-title {
    font-size: 1.6rem;
  }

  .current-price {
    font-size: 1.5rem;
  }

  .price-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .action-buttons {
    flex-direction: column;
    gap: 10px;
  }

  .add-to-cart-btn,
  .learn-more-btn {
    width: 100%;
    min-width: auto;
  }

  .product-highlights {
    padding: 40px 32px;
  }

  .highlights-title {
    font-size: 1.75rem;
  }

  .highlight-content {
    font-size: 1.05rem;
  }
}

@media (max-width: 640px) {
  .page-header {
    height: 56px !important;
  }

  .page-header h1 {
    font-size: 1.2rem;
  }

  .main-content {
    padding: 24px 16px;
  }

  .product-card {
    padding: 24px 20px;
    border-radius: var(--radius-lg);
    gap: 24px;
  }

  .main-image-wrapper {
    width: 100%;
    height: 320px;
    min-height: 280px;
    padding: 16px;
  }

  .thumbnail-list {
    max-width: 100%;
    gap: 8px;
  }

  .thumbnail-item {
    width: 64px;
    height: 64px;
  }

  .thumbnail-item img {
    max-height: 56px;
  }

  .product-title {
    font-size: 1.4rem;
  }

  .current-price {
    font-size: 1.5rem;
  }

  .price-section {
    padding: 12px 0;
  }

  .info-label {
    min-width: auto;
    font-size: 0.85rem;
  }

  .info-value {
    font-size: 0.85rem;
  }

  .info-row {
    flex-direction: column;
    gap: 4px;
  }

  .action-buttons {
    flex-direction: column;
    gap: 10px;
    margin-top: 12px;
  }

  .add-to-cart-btn,
  .learn-more-btn {
    width: 100%;
    padding: 10px 24px !important;
    font-size: 0.95rem !important;
  }

  .video-section,
  .contact-section {
    padding: 28px 20px;
    border-radius: var(--radius-lg);
  }

  .product-highlights {
    padding: 32px 24px;
    border-radius: var(--radius-lg);
  }

  .highlights-title,
  .contact-section h2 {
    font-size: 1.5rem;
    margin-bottom: 24px;
  }

  .highlights-content {
    gap: 20px;
  }

  .highlight-item {
    gap: 12px;
  }

  .check-icon {
    font-size: 18px;
    margin-right: 6px;
  }

  .highlight-content {
    font-size: 1rem;
    line-height: 1.6;
  }

  .contact-section p {
    font-size: 0.9rem;
  }

  .product-divider {
    margin: 40px 0;
  }

  .loading-state {
    min-height: 40vh;
    padding: 40px 16px;
  }

  .preview-close {
    top: 15px;
    right: 15px;
    width: 40px;
    height: 40px;
    font-size: 20px;
  }

  .preview-nav-btn {
    width: 44px !important;
    height: 44px !important;
  }

  .preview-nav-btn.prev-btn {
    left: 15px;
  }

  .preview-nav-btn.next-btn {
    right: 15px;
  }

  .preview-nav-btn i {
    font-size: 18px;
  }

  .preview-nav-btn:hover {
    transform: translateY(-50%) scale(1.05) !important;
  }

  .preview-image {
    max-width: 95vw;
    max-height: 85vh;
  }
}

</style>


